import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../themes/chartTheme';
import { Box, useTheme, ToggleButtonGroup, ToggleButton, Typography } from '@mui/material';
import { tokens } from '../theme';
import { FilterContext, IFilterState } from '../contexts/FilterContext';
import fetchData from '../helpers/fetchData';
import dayjs from 'dayjs';

interface IDoiRateOverTime {
  date: string;
  doi_rate: number;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
}

interface IDoiRateByEmailDomainOverTime extends IDoiRateOverTime {
  email_domain: string;
}

interface ISerie {
  id: string;
  data: { x: string; y: number }[];
}

interface DoiRateOverTimeChartOptinEmailProps {
  optInEmailId: number;
  emailDomain?: string;
  abTestGmail?: string;
}

type AggregationType = 'daily' | 'weekly' | 'monthly';

// Determine the appropriate aggregation based on date range
const determineAggregation = (startDate: Date, endDate: Date): AggregationType => {
  const diffInDays = dayjs(endDate).diff(dayjs(startDate), 'day');

  if (diffInDays > 120) {
    return 'monthly';
  } else if (diffInDays > 45) {
    return 'weekly';
  } else {
    return 'daily';
  }
};

// Format the date for display based on aggregation type
const formatDisplayDate = (dateString: string, aggregation: AggregationType): string => {
  switch (aggregation) {
    case 'monthly':
      // For YYYY-MM format
      const [year, month] = dateString.split('-');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${monthNames[parseInt(month) - 1]} ${year}`;

    case 'weekly':
      // For YYYY-WW format
      const [weekYear, weekNum] = dateString.split('-');
      return `W${weekNum} ${weekYear}`;

    case 'daily':
    default:
      // Keep YYYY-MM-DD format but make it more readable
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dayjs(dateString).format('DD.MM.YYYY');
      }
      return dateString;
  }
};

const DoiRateOverTimeChartOptinEmail: React.FC<DoiRateOverTimeChartOptinEmailProps> = ({
  optInEmailId,
  emailDomain,
  abTestGmail,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const [chartData, setChartData] = useState<ISerie[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [suggestedAggregation, setSuggestedAggregation] = useState<AggregationType>('daily');
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | null>(null);

  // The actual aggregation to use is either manually selected or the suggested one
  const aggregation = selectedAggregation || suggestedAggregation;

  // Fetch data based on the current aggregation type
  const fetchChartData = async () => {
    if (!filterState.startDate || !filterState.endDate) {
      throw new Error('Start and end dates are required');
    }

    setLoading(true);

    const options: any = {
      optInEmailId,
      startDate: filterState.startDate.toISOString().split('T')[0],
      endDate: filterState.endDate.toISOString().split('T')[0],
      aggregation: aggregation,
    };

    if (abTestGmail && abTestGmail !== 'all') {
      options.abTestGmail = abTestGmail;
    }

    try {
      // Create a common processing function to handle both types of data
      const processRateData = (items: Array<any>) => {
        if (!Array.isArray(items)) {
          items = [];
        }

        return items.map((item) => ({
          x: formatDisplayDate(item.date, aggregation),
          y: item.doi_rate ? parseFloat(String(item.doi_rate)) * 100 : 0,
        }));
      };

      let seriesData: { x: string; y: number }[] = [];

      if (emailDomain) {
        const endpoint = '/api/doi-evaluation-optin-emails/rates-by-email-domain-over-time';
        try {
          const response = await fetchData<any>(endpoint, { ...options, emailDomain }, true);
          seriesData = processRateData(response);
        } catch (err) {
          console.error('Error fetching email domain DOI rates:', err);
          seriesData = [];
        }
      } else {
        const endpoint = '/api/doi-evaluation-optin-emails/rates-over-time';
        try {
          const response = await fetchData<any>(endpoint, options, true);
          seriesData = processRateData(response);
        } catch (err) {
          console.error('Error fetching DOI rates:', err);
          seriesData = [];
        }
      }

      const processedData: ISerie[] = [
        {
          id: emailDomain ? `DOI Rate (${emailDomain})` : 'DOI Rate',
          data: seriesData,
        },
      ];
      setChartData(processedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching and processing DOI rate data: ', error);
      setLoading(false);
    }
  };

  // Update suggested aggregation when date range changes
  useEffect(() => {
    if (filterState.startDate && filterState.endDate) {
      const newSuggestedAggregation = determineAggregation(
        filterState.startDate,
        filterState.endDate,
      );
      setSuggestedAggregation(newSuggestedAggregation);

      // Reset selected aggregation when date range changes significantly
      if (newSuggestedAggregation !== suggestedAggregation) {
        setSelectedAggregation(null);
      }
    }
  }, [filterState.startDate, filterState.endDate]);

  // Fetch data when aggregation or filters change
  useEffect(() => {
    fetchChartData();
  }, [filterState, optInEmailId, emailDomain, aggregation]);

  // Handle manual aggregation selection
  const handleAggregationChange = (
    event: React.MouseEvent<HTMLElement>,
    newAggregation: AggregationType | null,
  ) => {
    if (newAggregation !== null) {
      setSelectedAggregation(newAggregation);
    }
  };

  if (loading) {
    return <p>Loading chart data...</p>;
  }

  if (chartData.length === 0 || chartData[0].data.length === 0) {
    return <p>No data available for the selected time period.</p>;
  }

  const getAggregationLabel = (agg: AggregationType): string => {
    switch (agg) {
      case 'monthly':
        return 'Monatlich';
      case 'weekly':
        return 'Wöchentlich';
      case 'daily':
      default:
        return 'Täglich';
    }
  };

  return (
    <Box>
      <Box mb={1} mt={0} display="flex" justifyContent="space-between" alignItems="center">
        <ToggleButtonGroup
          value={selectedAggregation || suggestedAggregation}
          exclusive
          onChange={handleAggregationChange}
          aria-label="Aggregation Type"
          size="small"
        >
          <ToggleButton value="daily" aria-label="Daily">
            Täglich
          </ToggleButton>
          <ToggleButton value="weekly" aria-label="Weekly">
            Wöchentlich
          </ToggleButton>
          <ToggleButton value="monthly" aria-label="Monthly">
            Monatlich
          </ToggleButton>
        </ToggleButtonGroup>

        {!selectedAggregation && (
          <Typography
            variant="caption"
            sx={{
              color: colors.grey[400],
              fontSize: '0.75rem',
              fontStyle: 'italic',
            }}
          >
            Automatisch gewählt: {getAggregationLabel(suggestedAggregation)}
          </Typography>
        )}
      </Box>

      <Box height="400px">
        <ResponsiveLine
          data={chartData}
          margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 100,
            stacked: false,
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: 'Date',
            legendPosition: 'middle',
            legendOffset: 40,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'DOI Rate (%)',
            legendOffset: -40,
            legendPosition: 'middle',
            format: (value) => `${value}%`,
          }}
          enableGridX={false}
          colors={{ scheme: 'category10' }}
          pointSize={10}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          theme={chartTheme}
          lineWidth={3}
          enableArea={true}
          areaOpacity={0.15}
          curve="monotoneX"
          tooltip={({ point }) => {
            // Create a tooltip containing date and value
            const dateText = `Date: ${point.data.x}`;
            const valueText = `${point.serieId}: ${parseFloat(point.data.yFormatted as string).toFixed(2)}%`;

            return (
              <div
                style={{
                  padding: '5px 10px',
                  color: '#222',
                  background: '#ffffff',
                  boxShadow: '0 0 2px #222',
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <p style={{ margin: 0 }}>
                  <strong>{dateText}</strong>
                </p>
                <p style={{ margin: 0 }}>
                  <strong>{valueText}</strong>
                </p>
              </div>
            );
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 100,
              translateY: 0,
              itemsSpacing: 0,
              itemDirection: 'left-to-right',
              itemWidth: 80,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default DoiRateOverTimeChartOptinEmail;
